import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import PencomTextField from "@/components/input/PencomTextField";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAddGroupLifeInsurance,
  useGetGroupLifeInsurance,
  useUpdateGroupLifeInsurance,
} from "@/hooks/compliance/compliance.hook";
import PencomDropdown from "@/components/input/PencomDropdown";
import PencomDateField from "@/components/input/PencomDateField";
import PencomFileUpload from "@/components/input/PencomFileUpload";
import { useForm } from "@tanstack/react-form";
import type { CreateGroupLifeInsuranceForm } from "@/forms/compliance/compliance.form";
import type { AddGroupLifeInsuranceDto } from "@/models/dto/groupLifeInsurance.dto";
import { toast } from "@/utils/toast";
import { useAuthStore } from "@/stores/authStore";
import { useMemo, useEffect } from "react";
import { PencomConstants } from "@/constants/PencomConstants";
import { formatNumberWithCommas, removeCommas } from "@/utils/numberFormatter";
import { getPolicyEndDateMax } from "@/utils/dateFormatter";
import { useGroupLifeInsuranceCertificates } from "@/hooks/group-life-insurance/group-life-insurance.hook";

export const AddGroupLifeInsurancePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stateData = location.state as { isUpdate: any } | null;
  const isUpdate = stateData?.isUpdate;

  const { mutate: addGroupLifeInsurance, isPending } =
    useAddGroupLifeInsurance();
  const {
    mutate: updateGroupLifeInsurance,
    isPending: isUpdatingGroupLifeInsurance,
  } = useUpdateGroupLifeInsurance();
  const { currentEmployer } = useAuthStore();
  const { data: existingGLI } = useGetGroupLifeInsurance();
  const {
    selectedFiles,
    certificateUrls,
    setCertificateUrls,
    isUploadingCertificate,
    handleFilesSelect,
    handleFileRemoveAt,
  } = useGroupLifeInsuranceCertificates();

  const handleAddGroupLifeInsurance = (value: CreateGroupLifeInsuranceForm) => {
    if (certificateUrls.length === 0) {
      toast.error("Please upload at least one insurance certificate");
      return;
    }

    const formData: AddGroupLifeInsuranceDto = {
      employer_id: currentEmployer?.employerId || "",
      commencement_date: value.commencement_date,
      expiry_date: value.expiry_date,
      number_of_lives_covered: value.number_of_lives_covered,
      insurance_company: value.insurance_company,
      insurance_company_phone_number: value.insurance_company_phone_number,
      insurance_company_email_address:
        value.insurance_company_email_address.trim(),
      address: value.address,
      insurance_certificate: certificateUrls,
      policy_number: value.policy_number,
      sum_assured: value.sum_assured,
    };

    console.log({ formData });

    addGroupLifeInsurance(formData, {
      onSuccess: () => {
        toast.success("Group life insurance added successfully!");
        navigate(-1);
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to add group life insurance";
        toast.error(errorMessage);
      },
    });
  };

  const handleUpdateGroupLifeInsurance = (
    value: CreateGroupLifeInsuranceForm
  ) => {
    if (certificateUrls.length === 0) {
      toast.error("Please upload at least one insurance certificate");
      return;
    }

    const formData: AddGroupLifeInsuranceDto = {
      employer_id: currentEmployer?.employerId || "",
      commencement_date: value.commencement_date,
      expiry_date: value.expiry_date,
      number_of_lives_covered: value.number_of_lives_covered,
      insurance_company: value.insurance_company,
      insurance_company_phone_number: value.insurance_company_phone_number,
      insurance_company_email_address:
        value.insurance_company_email_address.trim(),
      address: value.address,
      insurance_certificate: certificateUrls,
      policy_number: value.policy_number,
      sum_assured: value.sum_assured,
    };

    console.log({ formData });

    updateGroupLifeInsurance(formData, {
      onSuccess: () => {
        toast.success("Group life insurance updated successfully!");
        navigate(-1);
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update group life insurance";
        toast.error(errorMessage);
      },
    });
  };

  // Get today's date in YYYY-MM-DD format (minimum date - no past dates allowed)
  const todayDate = useMemo(() => {
    return new Date().toISOString().split("T")[0];
  }, []);

  const policyEndDateMax = useMemo(() => getPolicyEndDateMax(), []);

  const form = useForm({
    defaultValues: {
      commencement_date: "",
      expiry_date: "",
      number_of_lives_covered: 0,
      sum_assured: "",
      insurance_company: "",
      insurance_company_phone_number: "",
      insurance_company_email_address: "",
      address: "",
      insurance_certificate: "",
      employer_id: currentEmployer?.id || "",
      policy_number: "",
    } as CreateGroupLifeInsuranceForm,
    onSubmit: async ({ value }) => {
      if (isUpdate) {
        handleUpdateGroupLifeInsurance(value);
      } else {
        handleAddGroupLifeInsurance(value);
      }
    },
  });

  useEffect(() => {
    if (isUpdate && existingGLI?.data) {
      const gli = existingGLI.data;
      form.setFieldValue("commencement_date", gli.commencement_date || "");
      form.setFieldValue("expiry_date", gli.expiry_date || "");
      form.setFieldValue(
        "number_of_lives_covered",
        gli.number_of_lives_covered || 0
      );
      form.setFieldValue("sum_assured", gli.sum_assured?.toString() || "");
      form.setFieldValue("insurance_company", gli.insurance_company || "");
      form.setFieldValue(
        "insurance_company_phone_number",
        gli.insurance_company_phone_number || ""
      );
      form.setFieldValue(
        "insurance_company_email_address",
        gli.insurance_company_email_address || ""
      );
      form.setFieldValue("address", gli.address || "");
      form.setFieldValue("policy_number", gli.policy_number || "");
      if (gli.insurance_certificate?.length) {
        setCertificateUrls(gli.insurance_certificate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate, existingGLI?.data]);

  return (
    <div className="w-full flex flex-col min-h-screen">
      <div className="w-full flex flex-col  max-w-[635px] mx-auto mt-6">
        <div className="flex flex-col gap-y-4 items-center">
          <p className="font-semibold text-2xl font-clash-display text-medium-dark-grey">
            {isUpdate ? "Update Insurance Details" : "Add Insurance Details"}
          </p>
          <p className="font-regular font-clash-display text-sm text-medium-dark-grey">
            Provide your company's valid Group Life Insurance (GLI) details.
            These are required for PCC compliance and must match your insurance
            certificate.
          </p>
        </div>
        <div className="flex flex-col mt-8 gap-y-6">
          <form.Field
            name="commencement_date"
            validators={{
              onChange: ({ value }) => {
                if (!value) return "Commencement date is required.";

                const expiryDate = form.getFieldValue("expiry_date");
                if (expiryDate && value >= expiryDate) {
                  return "Commencement date must be before the policy end date.";
                }

                return undefined;
              },
            }}
            children={(field) => (
              <div className="flex-1">
                <PencomDateField
                  id="commencement-date"
                  label="Policy Start Date"
                  placeholder="Select policy start date"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-500 text-xs mt-1">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          />
          <form.Field
            name="expiry_date"
            validators={{
              onChange: ({ value }) => {
                if (!value) return "Expiry date is required.";

                // Check if date is in the past
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Reset time to compare dates only

                if (selectedDate < today) {
                  return "Expiry date cannot be in the past.";
                }

                return undefined;
              },
            }}
            children={(field) => (
              <div className="flex-1">
                <PencomDateField
                  id="expiry-date"
                  label="Policy End Date"
                  placeholder="Select policy end date"
                  value={field.state.value}
                  onChange={(value) => field.handleChange(value)}
                  min={todayDate}
                  max={policyEndDateMax}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-red-500 text-xs mt-1">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          />
          <div className="flex flex-row gap-x-11">
            <form.Field
              name="number_of_lives_covered"
              validators={{
                onChange: ({ value }) =>
                  value <= 0
                    ? "Number of lives covered is required."
                    : undefined,
              }}
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="number-of-lives-covered"
                    label="Number of Lives Covered"
                    descriptionText="Enter the number of employees covered under this policy. This should match or exceed your total staff count."
                    placeholder="30"
                    type="text"
                    value={formatNumberWithCommas(field.state.value.toString())}
                    onChange={(e) => {
                      const rawValue = removeCommas(e.target.value);
                      field.handleChange(parseInt(rawValue) || 0);
                    }}
                    helperText={
                      field.state.meta.errors.length > 0
                        ? field.state.meta.errors[0]
                        : undefined
                    }
                  />
                </div>
              )}
            />
            <form.Field
              name="sum_assured"
              validators={{
                onChange: ({ value }) => {
                  if (!value || value.trim() === "") {
                    return "Sum assured is required.";
                  }
                  if (Number(value) <= 0) {
                    return "Sum assured must be greater than 0.";
                  }
                  return undefined;
                },
              }}
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="sum-assured"
                    label="Total Sum Assured (₦)"
                    placeholder="300,000"
                    type="text"
                    descriptionText="This is the benefit amount paid per employee in the event of death, as stated on the certificate."
                    value={formatNumberWithCommas(field.state.value)}
                    onChange={(e) => {
                      const rawValue = removeCommas(e.target.value);
                      field.handleChange(rawValue);
                    }}
                    helperText={
                      field.state.meta.errors.length > 0
                        ? field.state.meta.errors[0]
                        : undefined
                    }
                  />
                </div>
              )}
            />
          </div>
          <div className="flex flex-row gap-x-11">
            <form.Field
              name="insurance_company"
              validators={{
                onChange: ({ value }) =>
                  !value || value === ""
                    ? "Insurance company is required."
                    : undefined,
              }}
              children={(field) => (
                <div className="flex-1">
                  <PencomDropdown
                    id="insurance-company"
                    label="Insurance Company"
                    placeholder="Select insurance company"
                    options={PencomConstants.GROUP_LIFE_PROVIDERS.map(
                      (provider) => ({
                        id: provider.id,
                        label: provider.name,
                      })
                    )}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    helperText={
                      field.state.meta.errors.length > 0
                        ? field.state.meta.errors[0]
                        : undefined
                    }
                  />
                </div>
              )}
            />
            <form.Field
              name="insurance_company_phone_number"
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="insurance-company-phone-number"
                    label="Insurance Company Phone Number (Optional)"
                    placeholder="08012345678"
                    type="text"
                    value={form.state.values.insurance_company_phone_number}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 11);
                      field.handleChange(value);
                    }}
                  />
                </div>
              )}
            />
          </div>
          <div className="flex flex-row gap-x-11">
            <form.Field
              name="insurance_company_email_address"
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="insurance-company-email-address"
                    label="Insurance Company Email Address (Optional)"
                    placeholder="example@email.com"
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            />
            <form.Field
              name="address"
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="address"
                    label="Address (Optional)"
                    placeholder="123 Main St, Lagos"
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            />
          </div>
          <div className="flex flex-row gap-x-11">
            <form.Field
              name="policy_number"
              validators={{
                onChange: ({ value }) =>
                  !value || value.trim() === ""
                    ? "Policy number is required."
                    : undefined,
              }}
              children={(field) => (
                <div className="flex-1">
                  <PencomTextField
                    id="policy-number"
                    label="Policy Number"
                    descriptionText="Enter the exact policy number on your certificate. "
                    placeholder="POL-123456"
                    type="text"
                    value={field.state.value}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 12);
                      field.handleChange(value);
                    }}
                    helperText={
                      field.state.meta.errors.length > 0
                        ? field.state.meta.errors[0]
                        : undefined
                    }
                  />
                </div>
              )}
            />
          </div>
          <div className="rounded-lg border border-stroke bg-[#F9F9FB] p-4 font-clash-display text-sm font-semibold text-medium-dark-grey">
            <p>Upload the following documents:</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>GLI Certificate</li>
              <li>Schedule of Life</li>
              <li>Evidence of Payment</li>
            </ul>
          </div>

          <PencomFileUpload
            id="insurance-certificate-upload"
            label="Documents"
            placeholder="Click here or drag and drop to upload documents"
            accept=".pdf,.jpg,.jpeg,.png"
            maxSize={10}
            maxFiles={1000}
            onFilesSelect={handleFilesSelect}
            selectedFiles={selectedFiles}
            className=""
            disabled={isUploadingCertificate}
            onFileRemoveAt={handleFileRemoveAt}
            hint="You need to upload at the least 3 documents"
          />
          <div className="flex flex-col items-center justify-center">
            <p className="font-bold font-clash-display text-sm text-medium-dark-grey">
              Complete all required fields to enable submission.
            </p>
          </div>
          <PrimaryButton
            className="mt-10"
            title={
              isUploadingCertificate
                ? "Uploading Certificates..."
                : isPending || isUpdatingGroupLifeInsurance
                ? "Submitting..."
                : "Submit to PenCom"
            }
            onClick={() => form.handleSubmit()}
            disabled={
              isPending ||
              isUploadingCertificate ||
              certificateUrls.length === 0 ||
              isUpdatingGroupLifeInsurance
            }
          />
        </div>
      </div>
    </div>
  );
};
